import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Navigation, CheckCircle2, MapPin, Loader2 } from 'lucide-react';
import api, { getAccessToken } from '../lib/api';

// Custom pins
const trainerPin = L.divIcon({
  className: '',
  html: `
    <div style="position:relative;width:32px;height:32px;">
      <div style="position:absolute;inset:0;border-radius:9999px;background:#C0A062;opacity:0.35;animation:dgPulse 1.6s ease-out infinite;"></div>
      <div style="position:absolute;inset:6px;border-radius:9999px;background:#C0A062;border:2px solid #18181B;box-shadow:0 2px 8px rgba(0,0,0,0.6);"></div>
    </div>
    <style>@keyframes dgPulse{0%{transform:scale(0.6);opacity:0.7}100%{transform:scale(2);opacity:0}}</style>
  `,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

const destinationPin = L.divIcon({
  className: '',
  html: `
    <div style="position:relative;width:28px;height:36px;">
      <svg viewBox="0 0 24 32" width="28" height="36" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 0c6.6 0 12 5.4 12 12 0 9-12 20-12 20S0 21 0 12C0 5.4 5.4 0 12 0z" fill="#ef4444"/>
        <circle cx="12" cy="12" r="4.5" fill="#18181B"/>
      </svg>
    </div>
  `,
  iconSize: [28, 36],
  iconAnchor: [14, 36],
});

const Recenter = ({ trainer, destination }) => {
  const map = useMap();
  useEffect(() => {
    const points = [];
    if (trainer) points.push([trainer.lat, trainer.lng]);
    if (destination) points.push([destination.lat, destination.lng]);
    if (points.length === 2) {
      map.fitBounds(points, { padding: [40, 40], maxZoom: 15 });
    } else if (points.length === 1) {
      map.setView(points[0], 14);
    }
  }, [trainer, destination, map]);
  return null;
};

const ClientTrackingMap = ({ session, onArrived }) => {
  const [trainerLoc, setTrainerLoc] = useState(session?.trainer_last_location || null);
  const [enRoute, setEnRoute] = useState(!!session?.trainer_en_route);
  const [arrived, setArrived] = useState(false);
  const [loading, setLoading] = useState(true);
  const wsRef = useRef(null);
  const pollRef = useRef(null);

  const destination = (() => {
    if (session?.location_lat && session?.location_lng) {
      return { lat: session.location_lat, lng: session.location_lng };
    }
    return null;
  })();

  const fetchState = useCallback(async () => {
    try {
      const res = await api.get(`/sessions/${session.id}/tracking`);
      if (res.data.trainer_last_location) setTrainerLoc(res.data.trainer_last_location);
      setEnRoute(!!res.data.trainer_en_route);
      if (res.data.trainer_arrived_at) {
        setArrived(true);
        setEnRoute(false);
        onArrived?.();
      }
    } catch (e) {
      // fall through
    } finally {
      setLoading(false);
    }
  }, [session.id, onArrived]);

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  useEffect(() => {
    if (!enRoute || arrived) return undefined;

    const httpUrl = process.env.REACT_APP_BACKEND_URL;
    const token = getAccessToken();
    if (!httpUrl) return undefined;
    const wsUrl = `${httpUrl.replace(/^http/, 'ws')}/api/ws/track/${session.id}?token=${encodeURIComponent(token || '')}`;

    let stopped = false;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'location' && msg.location) {
          setTrainerLoc(msg.location);
        } else if (msg.type === 'arrived') {
          setEnRoute(false);
          setArrived(true);
          onArrived?.();
        }
      } catch (_) { /* noop */ }
    };
    ws.onerror = () => { /* polling fallback handles it */ };
    ws.onclose = () => {
      if (stopped) return;
    };

    // Safety poll every 12s in case WS drops
    pollRef.current = setInterval(fetchState, 12_000);

    return () => {
      stopped = true;
      try { ws.close(); } catch (_) { /* noop */ }
      wsRef.current = null;
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [enRoute, arrived, session.id, fetchState, onArrived]);

  if (loading) {
    return (
      <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6 flex items-center gap-2 text-zinc-500 text-sm" data-testid="tracking-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Checking trainer status…
      </div>
    );
  }

  if (!enRoute && !arrived) return null;

  if (arrived) {
    return (
      <div
        className="bg-[#18181B] border border-emerald-500/30 rounded-lg p-4 flex items-center gap-3"
        data-testid="trainer-arrived-banner"
      >
        <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
        <p className="text-sm text-emerald-200">Your trainer has arrived.</p>
      </div>
    );
  }

  const showMap = !!trainerLoc && trainerLoc.lat && trainerLoc.lng;

  return (
    <div className="bg-[#18181B] border border-[#C0A062]/30 rounded-lg overflow-hidden" data-testid="client-tracking-panel">
      <div className="p-4 border-b border-zinc-800 flex items-center gap-3">
        <div className="relative w-9 h-9 rounded-full bg-[#C0A062]/15 border border-[#C0A062]/40 flex items-center justify-center shrink-0">
          <Navigation className="w-4 h-4 text-[#C0A062]" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-white">Your trainer is on the way!</p>
          <p className="text-xs text-zinc-400">
            {showMap
              ? 'Live location updating every few seconds.'
              : 'Your trainer has marked themselves as on the way.'}
          </p>
        </div>
      </div>

      {showMap && (
        <div className="h-[280px] sm:h-[360px] w-full" data-testid="client-tracking-map">
          <MapContainer
            center={[trainerLoc.lat, trainerLoc.lng]}
            zoom={14}
            scrollWheelZoom={false}
            style={{ height: '100%', width: '100%', background: '#0d0d10' }}
          >
            <TileLayer
              attribution="&copy; OpenStreetMap"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={[trainerLoc.lat, trainerLoc.lng]} icon={trainerPin} />
            {destination && (
              <Marker position={[destination.lat, destination.lng]} icon={destinationPin} />
            )}
            <Recenter trainer={trainerLoc} destination={destination} />
          </MapContainer>
        </div>
      )}

      {session?.location_address && (
        <div className="px-4 py-3 border-t border-zinc-800 text-xs text-zinc-500 flex items-start gap-2">
          <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>Destination: {session.location_address}</span>
        </div>
      )}
    </div>
  );
};

export default ClientTrackingMap;
